/**
 * Pinia notification store — agreguje stored notifications z backend
 * `GET /api/notifications` a real-time updates z WS hub.
 *
 * **Aktuálně:** primárně polling 60s. Real-time WS subscribe na
 * `user:<uid>:notifications` topic je připraven, ale vyžaduje backend ObjectId
 * v session (portal session má jen intId — Laravel compat). Až bude backend
 * podporovat `user:self:notifications` self-alias, frontend si snadno připojí
 * přes `subscribeRealtime()` action — kostra je hotová.
 *
 * Backend response shape (per backend `app/api/notifications.py`):
 * - etalon-heuristic items: `{id, type, title, message, etalonId?, createdAt}`
 * - stored notifications: `{id, type, title, message, entityRef, metadata,
 *   channels, readAt, createdAt, source: "stored"}`
 *
 * Frontend zobrazuje obě varianty stejně; `source: "stored"` je interně
 * pro mark-as-read endpoint (POST /api/notifications/{id}/read).
 */
import { defineStore } from 'pinia';
import { useAxios } from '@/axios';
import useWebSocket, { type WSSubscription } from '@/composables/useWebSocket';

export type Notification = {
    id: string;
    type: string;
    title?: string | null;
    message: string;
    entityRef?: Record<string, unknown>;
    metadata?: Record<string, unknown>;
    channels?: string[];
    readAt?: string | null;
    createdAt?: string;
    source?: 'stored' | 'etalon';
    etalonId?: string;
};

const POLL_INTERVAL_MS = 60_000;

export const useNotificationStore = defineStore('notifications', {
    state: () => ({
        items: [] as Notification[],
        loading: false as boolean,
        pollTimer: null as ReturnType<typeof setInterval> | null,
        wsSubscription: null as WSSubscription | null,
    }),
    getters: {
        unreadCount(state): number {
            return state.items.filter((n) => !n.readAt && n.source === 'stored').length;
        },
        totalCount(state): number {
            return state.items.length;
        },
    },
    actions: {
        async fetchNotifications(): Promise<void> {
            try {
                this.loading = true;
                const axios = useAxios();
                const { data } = await axios.get<Notification[]>('/notifications');
                this.items = Array.isArray(data) ? data : [];
            } catch {
                // silent — polling zkusí znovu
            } finally {
                this.loading = false;
            }
        },

        async markRead(notifId: string): Promise<void> {
            const target = this.items.find((n) => n.id === notifId && n.source === 'stored');
            if (!target || target.readAt) return;
            try {
                const axios = useAxios();
                await axios.post(`/notifications/${notifId}/read`);
                target.readAt = new Date().toISOString();
            } catch {
                // ignorujeme; další refetch to vyřeší
            }
        },

        startPolling(): void {
            if (this.pollTimer) return;
            void this.fetchNotifications();
            this.pollTimer = setInterval(() => {
                void this.fetchNotifications();
            }, POLL_INTERVAL_MS);
        },

        stopPolling(): void {
            if (this.pollTimer) {
                clearInterval(this.pollTimer);
                this.pollTimer = null;
            }
        },

        /**
         * Volitelný real-time subscribe (vyžaduje backend ObjectId v topic).
         * Pro Fázi 11 MVP NEvoláme — polling je dostatečný a backend nemá
         * self-alias podporu pro user topic. Až bude, stačí v komponentě
         * volat tuto akci s plným topic stringem.
         */
        subscribeRealtime(userObjectId: string): void {
            if (this.wsSubscription) return;
            const { subscribe } = useWebSocket();
            this.wsSubscription = subscribe(
                `user:${userObjectId}:notifications`,
                (event) => {
                    if (event.event === 'notification.new') {
                        void this.fetchNotifications();
                    }
                }
            );
        },

        unsubscribeRealtime(): void {
            this.wsSubscription?.unsubscribe();
            this.wsSubscription = null;
        },

        reset(): void {
            this.stopPolling();
            this.unsubscribeRealtime();
            this.items = [];
        },
    },
});
